import { FieldValues, useController, useFormContext } from 'react-hook-form';

import { BaseSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select';
import { useLocalOptions } from '@/shared/components/ui/form/components/searchable-select/hooks/use-local-options/use-local-options';
import { useSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/hooks/use-searchable-select/use-searchable-select';
import { TLocalSearchableSelectProps } from '@/shared/components/ui/form/components/searchable-select/variant/local/local-searchable-select.types';
import { useDebounce } from '@/shared/hooks/use-debounce/use-debounce';

export const LocalSearchableSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  label,
  placeholder,
  disabled,
  required,
  customLabel,
  multiple,
  disableTags,
  enableTagsTooltip,
  filterSelectedTags,
  options: rawOptions,
  onSelectChange,
  defaultValue,
  renderNoOptions,
  getItemId,
  hint,
}: TLocalSearchableSelectProps<TFieldValues, TOptionShape>) => {
  const { shouldSearch, inputValue, handleSelectChange, handleInputChange } =
    useSearchableSelect({ onSelectChange, multiple });
  const debouncedSearch = useDebounce({
    value: shouldSearch ? inputValue : '',
    delay: shouldSearch ? undefined : 0,
  });
  const { control } = useFormContext<TFieldValues>();
  const {
    field: { value },
  } = useController({
    name,
    control,
    defaultValue,
  });

  const { options, isSelected } = useLocalOptions<TOptionShape>({
    debouncedSearch,
    options: rawOptions,
    getItemId,
    value,
  });

  return (
    <BaseSearchableSelect
      name={name}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      customLabel={customLabel}
      multiple={multiple}
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      onSelectChange={handleSelectChange}
      defaultValue={defaultValue}
      renderNoOptions={renderNoOptions}
      getItemId={getItemId}
      options={options}
      loading={false}
      isSelected={isSelected}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      hint={hint}
    />
  );
};
